import React, { useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Card,
  Grid,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { InfoOutlined, Close } from '@mui/icons-material';
import { Col, Row } from 'antd';
import {
  UserOutlined,
  IdcardOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  StarOutlined,
  CalendarOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { PrintOutlined } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment-timezone';
import { Tooltip } from 'antd';
import { useCurrentOrg } from 'store/organizations/selectors';
import Loading from 'components/Loading';
import { usePlacementOrgConfig } from 'store/placement_exam/selectors';
import {
  getNativeNonNativeConfigRows,
  getNativeNonNativeLabelFromPoints,
} from 'components/PlacementExam/NativeNonNativeConfiguration';
import {
  getProficiencyConfigRows,
  getOverallProficiencyLabelForSubmission,
  getZeroScoreProficiencyLevelName,
  isPlacementZeroDomainCriteriaEnabled,
  isProficiencyBandConfigActive,
  PROFICIENCY_LEVEL_LABELS,
  sumMaxPointsForCategory,
  sumSubmissionCategoryPoints,
} from 'components/PlacementExam/ProficiencyConfiguration';
import { placement_exam_logo } from 'utils/assets/images';
import { disableBodyScroll } from 'utils/methods/math';
import COLOR from 'utils/constants/color';
import SentryWrap from 'components/Sentry';

const Wrapper = styled.div`
  margin: 0px auto;
  width: 100%;
  position: relative;
  top: 0px;
  min-height: 70vh;
  max-height: 92vh;
  overflow-y: auto;
  overflow-x: hidden;

  @media (min-width: 1300px) {
    width: 85%;
  }

  @media (min-width: 1000px) and (max-width: 1299px) {
    width: 85%;
  }

  @media (min-width: 700px) and (max-width: 999px) {
    width: 90%;
  }

  @media (max-width: 699px) {
    width: 100%;
  }
  @media print {
    width: 100% !important;
    position: static !important;
    top: 0 !important;
    min-height: auto !important;
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 10px !important;
    box-shadow: none !important;
    background: white !important;
    page-break-inside: avoid;
  }
`;

const InfoGrid = styled(Grid)`
  margin-bottom: 16px;
  background-color: ${COLOR.indigo100};
  color: #fff;
  min-height: 110px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
`;

const InfoRow = styled(Row)`
  width: 100%;
`;

const InfoCol = styled(Col)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  margin: 0;
  display: flex;
  @media (max-width: 1168px) {
    font-size: 14px;
  }
`;

const IconWrapper = styled.span`
  font-size: 20px;
  color: #fff;
`;

const UserValue = styled.div`
  font-size: 12px;
  font-weight: 200;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 3px;
  min-width: 200px;
`;

const PrintButton = styled(IconButton)`
  @media print {
    visibility: hidden !important;
    display: none !important;
  }
`;

const LogoImg = styled.img`
  max-width: 100%;
`;

const DefaultLogoImg = styled.img`
  height: 150px;
  width: auto;
  border-radius: 12px;
  border: 4px solid #03999e;
`;

const Section = styled.div`
  margin-bottom: 0px;
  page-break-inside: avoid;

  @media print {
    margin-bottom: 8px;
    page-break-inside: avoid;
    overflow: visible;
  }
`;

const StyledCard = styled(Card)`
  background-color: #f5f5ff;
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ProficiencyTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 600;
  color: ${COLOR.indigo100};
  margin-bottom: 24px;
  text-align: center;

  @media print {
    font-size: 18px;
    margin-bottom: 12px;
    page-break-after: avoid;
  }
`;

const SkillTableContainer = styled(TableContainer)`
  margin-top: 32px;
  margin-bottom: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #dfdfdfde;

  @media print {
    margin-top: 4px;
    margin-bottom: 16px;
    box-shadow: none;
    border: 1px solid #ccc !important;
    page-break-inside: avoid;
    overflow: visible;
    width: 100%;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const SkillTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;

  @media print {
    width: 100%;
    font-size: 10px;
    border-collapse: collapse;
  }
`;

const SkillTableHeader = styled(TableHead)`
  background-color: #f8fafc;

  @media print {
    background-color: #1e293b !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const SkillTableHeaderRow = styled(TableRow)`
  border-bottom: 2px solid #e2e8f0;
`;

const SkillTableHeaderCell = styled(TableCell)`
  padding: 16px 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  border-right: 1px dashed #cbd5e1;
  white-space: nowrap;

  &:first-child {
    text-align: left;
    padding-left: 16px;
    min-width: 150px;
  }

  &:last-child {
    border-right: none;
  }

  &.proficiency-header {
    min-width: 120px;
  }

  &.scale-score-header {
    min-width: 120px;
    color: #1e40af;
  }

  @media print {
    padding: 8px 6px;
    font-size: 9px;
    white-space: normal;
    word-wrap: break-word;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &:first-child {
      padding-left: 8px;
      min-width: 100px;
    }

    &.proficiency-header {
      min-width: 80px;
    }

    &.scale-score-header {
      min-width: 80px;
      color: #fff !important;
    }

    & > div {
      color: #fff !important;
    }
  }
`;

const SkillTableBodyRow = styled(TableRow)`
  border-bottom: 1px solid #e2e8f0;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  @media print {
    border-bottom: 1px solid #e2e8f0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const SkillTableCell = styled(TableCell)`
  padding: 16px 12px;
  text-align: center;
  font-size: 14px;
  color: #334155;
  border-right: 1px dashed #cbd5e1;
  vertical-align: middle;

  &:first-child {
    text-align: left;
    padding-left: 16px;
    font-weight: 600;
    color: #1e293b;
  }

  &:last-child {
    border-right: none;
  }

  @media print {
    padding: 8px 6px;
    font-size: 10px;
    color: #334155 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &:first-child {
      padding-left: 8px;
      color: #1e293b !important;
    }
  }
`;

const ProficiencyLevelCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const ProficiencyRange = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;

  @media print {
    font-size: 8px;
    line-height: 1.2;
    color: #64748b !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const ProficiencyBarWrapper = styled.div`
  width: 100%;
  height: 32px;
  position: relative;
  margin: 4px 0;
  display: flex;
  align-items: center;

  @media print {
    height: 24px;
    margin: 2px 0;
    overflow: visible;
  }
`;

const ProficiencyBarContainer = styled.div`
  height: 32px;
  position: absolute;
  left: ${(props) => props.leftPosition}%;
  width: ${(props) => props.width}%;
  background-color: #2563eb;
  border-radius: 4px;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;

  @media print {
    height: 24px;
    border-radius: 2px;
    background-color: #2563eb !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const ScaleScoreCell = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e40af;

  @media print {
    color: #1e40af !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const UserInfoWrapper = styled.div`
  position: relative;
  width: 100%;

  @media print {
    page-break-inside: avoid;
    margin-bottom: 16px;

    .studentInfoUpr {
      page-break-inside: avoid;
      overflow: visible;
    }
  }
`;

const PrintButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  padding-right: 0;

  @media print {
    display: none !important;
  }
`;

const OverallScoreDisplay = styled.div`
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);

  @media print {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const OverallScoreLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  display: block;

  @media print {
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const OverallScoreValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 4px;

  @media print {
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const ProficiencyLevelBadge = styled.span`
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;

  @media print {
    background-color: rgba(255, 255, 255, 0.2) !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const OverallProficiencySection = styled.div`
  position: absolute;
  bottom: 12px;
  right: 10px;
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  max-width: min(340px, calc(100% - 24px));
  z-index: 1;
`;

const OverallProficiencyBox = styled.div`
  background-color: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  padding: 3px 10px;
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 13px;
    font-weight: 400;
    color: #fff;
    margin: 0;
  }

  @media print {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    margin-top: 6px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const ZeroDomainProficiencyNote = styled.p`
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  font-weight: 400;
  color: #fca5a5;
  text-align: right;

  @media print {
    font-size: 10px;
    color: #f87171 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const DateProficiencyRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ProficiencyBarValue = styled.span`
  color: #ffffff !important;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media print {
    font-size: 10px;
    color: #ffffff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const LegendButtonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
  @media print {
    display: none !important;
  }
`;

const SKILL_AREA_LEGEND = [
  { label: 'Speaking', description: 'Speaking' },
  { label: 'Listening', description: 'Listening' },
  { label: 'Reading', description: 'Reading' },
  { label: 'Writing', description: 'Writing' },
  { label: 'Comprehension', description: 'Comprehension (Listening + Reading)' },
  { label: 'Vocal', description: 'Vocal (Listening + Speaking)' },
  { label: 'Literacy', description: 'Literacy (Reading + Writing)' },
  { label: 'Active', description: 'Active (Speaking + Writing)' },
];

const SkillAreaTable = ({ submission, assignment, overAllData }) => {
  const [legendOpen, setLegendOpen] = useState(false);
  const orgConfigForTable = usePlacementOrgConfig();
  const proficiencyExamCode = assignment?.assigned?.assignment_code;
  const profConfigRows = useMemo(
    () => getProficiencyConfigRows(orgConfigForTable, proficiencyExamCode),
    [orgConfigForTable, proficiencyExamCode]
  );
  const proficiencyLevels = useMemo(() => {
    const examMax = assignment?.questions?.length
      ? assignment.questions.reduce((s, q) => s + Number(q.max_point || 0), 0)
      : 0;
    const useBands = isProficiencyBandConfigActive(profConfigRows, examMax);
    return [0, 1, 2, 3, 4].map((i) => ({
      number: i + 1,
      label:
        useBands && profConfigRows[i] && String(profConfigRows[i].title || "").trim()
          ? String(profConfigRows[i].title).trim()
          : PROFICIENCY_LEVEL_LABELS[i] || `Level ${i + 1}`,
    }));
  }, [profConfigRows, assignment]);
  const domainConfig = assignment?.domainConfig || overAllData?.domainConfig || {};
  const max_point_speaking = useMemo(
    () =>
      assignment?.questions
        ?.filter((question) => question.category_type === 'speaking')
        ?.reduce((acc, curr) =>  curr.max_point ? acc + Number(curr.max_point) : 0, 0) || 0,
    [assignment],
  );
  const max_point_listening = useMemo(
    () =>
      assignment?.questions
        ?.filter((question) => question.category_type === 'listening')
        ?.reduce((acc, curr) =>  curr.max_point ? acc + Number(curr.max_point) : 0, 0) || 0,
    [assignment],
    
  );

  const max_point_reading = useMemo(
    () =>
      assignment?.questions
        ?.filter((question) => question.category_type === 'reading')
        ?.reduce((acc, curr) =>  curr.max_point ? acc + Number(curr.max_point) : 0, 0) || 0,
    [assignment],
  );
  const max_point_writing = useMemo(
    () =>
      assignment?.questions
        ?.filter((question) => question.category_type === 'writing')
        ?.reduce((acc, curr) =>  curr.max_point ? acc + Number(curr.max_point) : 0, 0) || 0,
    [assignment],
  );
  const generateRangesFromMaxPoint = (maxPoint) => {
    if (!maxPoint || maxPoint <= 0) {
      return [
        [0, 0],
        [0, 0],
        [0, 0],
        [0, 0],
        [0, 0],
      ];
    }

    const level1End = Math.floor(maxPoint * 0.2);
    const level2End = Math.floor(maxPoint * 0.4);
    const level3End = Math.floor(maxPoint * 0.6);
    const level4End = Math.floor(maxPoint * 0.8);

    return [
      [0, level1End],
      [level1End + 1, level2End],
      [level2End + 1, level3End],
      [level3End + 1, level4End],
      [level4End + 1, maxPoint],
    ];
  };

  const generateRangesFromDomainConfig = (config, maxPoint) => {
    if (!config || typeof config !== 'object' || !maxPoint || maxPoint <= 0) {
      return generateRangesFromMaxPoint(maxPoint);
    }
    const keys = ['novice', 'early_intermediate', 'intermediate', 'advance', 'advance_high'];
    const ranges = keys.map((key) => {
      const from = Number(config[key]?.from) ?? 0;
      const to = Number(config[key]?.to) ?? 0;
      return [from, to];
    });
    const valid = ranges.every(([a, b]) => !Number.isNaN(a) && !Number.isNaN(b));
    return valid ? ranges : generateRangesFromMaxPoint(maxPoint);
  };

  const getBarWidthFromRanges = (value, ranges) => {
    if (!Array.isArray(ranges) || ranges.length === 0) {
      return 0;
    }
    const totalLevels = ranges.length;
    const levelWidth = 100 / totalLevels;
    const numericValue = Number(value) || 0;
    for (let index = 0; index < ranges.length; index += 1) {
      const [fromRaw, toRaw] = ranges[index];
      const from = Number(fromRaw);
      const to = Number(toRaw);
      if (Number.isNaN(from) || Number.isNaN(to)) {
        continue;
      }
      if (numericValue >= from && numericValue <= to) {
        if (to === from) {
          return Math.min(100, (index + 1) * levelWidth);
        }
        const levelProgress = (numericValue - from) / (to - from);
        return Math.min(100, (index * levelWidth) + (levelProgress * levelWidth));
      }
    }
    const firstFrom = Number(ranges[0]?.[0]);
    if (!Number.isNaN(firstFrom) && numericValue < firstFrom) {
      return 0;
    }
    return 100;
  };

  const calculateBarWidth = (categoryType) => {
    if (!assignment?.questions || !submission?.ai_grade) {
      return { barWidth: 0, rangeValue: 0 };
    }
    const categorySections = assignment.questions
      .map((section, index) => ({ section, sectionIndex: index }))
      .filter(({ section }) => section.category_type === categoryType);

    let totalPoints = 0;
    categorySections.forEach((section) => {
      if (section?.section?.rubric_id) {
        const ai_dt = submission?.ai_grading_by_sections[section.sectionIndex];
        if (ai_dt) {
          totalPoints += Number(ai_dt?.grade_point) || 0;
        }
      } else {
        const submissionAnswer = submission.ai_grade[section.sectionIndex];
        if (submissionAnswer && Array.isArray(submissionAnswer)) {
          totalPoints += submissionAnswer.reduce((acc, curr) => acc + (Number(curr?.ai_grade_point) || 0), 0);
        }
      }
    });

    return {
      barWidth: 0,
      rangeValue: totalPoints,
    };
  };

  const calculateCombinedBarWidth = (categoryType1, categoryType2) => {
    if (!assignment?.questions || !submission?.ai_grade) {
      return { barWidth: 0, rangeValue: 0 };
    }
    const sections1 = assignment.questions
      .map((section, index) => ({ section, sectionIndex: index }))
      .filter(({ section }) => section.category_type === categoryType1);
    const sections2 = assignment.questions
      .map((section, index) => ({ section, sectionIndex: index }))
      .filter(({ section }) => section.category_type === categoryType2);
    const allSections = [...sections1, ...sections2];
    const max_points = allSections.reduce((acc, curr) => acc + curr.section.max_point, 0);
    if (max_points === 0) {
      return { barWidth: 0, rangeValue: 0 };
    }
    let totalPoints = 0;
    allSections.forEach((section) => {
      if (section?.section?.rubric_id) {
        const ai_dt = submission?.ai_grading_by_sections[section.sectionIndex];
        if (ai_dt) {
          totalPoints += Number(ai_dt?.grade_point) || 0;
        }
      } else {
        const submissionAnswer = submission.ai_grade[section.sectionIndex];
        if (submissionAnswer && Array.isArray(submissionAnswer)) {
          totalPoints += submissionAnswer.reduce(
            (acc, curr) => acc + (Number(curr?.ai_grade_point) || 0),
            0,
          );
        }
      }
    });
    return {
      barWidth: 0,
      rangeValue: totalPoints,
    };
  };
  

  const skillData = useMemo(() => {
    const speakingBar = calculateBarWidth('speaking');
    const listeningBar = calculateBarWidth('listening');
    const readingBar = calculateBarWidth('reading');
    const writingBar = calculateBarWidth('writing');
    const comprehensionBar = calculateCombinedBarWidth('listening', 'reading');
    const vocalBar = calculateCombinedBarWidth('listening', 'speaking');
    const literacyBar = calculateCombinedBarWidth('reading', 'writing');
    const activeBar = calculateCombinedBarWidth('speaking', 'writing');

    const speakingRanges = generateRangesFromDomainConfig(domainConfig?.speaking, max_point_speaking);
    const listeningRanges = generateRangesFromDomainConfig(domainConfig?.listening, max_point_listening);
    const readingRanges = generateRangesFromDomainConfig(domainConfig?.reading, max_point_reading);
    const writingRanges = generateRangesFromDomainConfig(domainConfig?.writing, max_point_writing);
    const comprehensionRanges = generateRangesFromDomainConfig(domainConfig?.comprehension,Number(max_point_listening) + Number(max_point_reading));
    const vocalRanges = generateRangesFromDomainConfig(domainConfig?.vocal,Number(max_point_listening) + Number(max_point_speaking));
    const literacyRanges = generateRangesFromDomainConfig(domainConfig?.literacy,Number(max_point_reading) + Number(max_point_writing));
    const activeRanges = generateRangesFromDomainConfig(domainConfig?.active,Number(max_point_speaking) + Number(max_point_writing));

    const defaultRanges = {
      Speaking: {
        score: max_point_speaking,
        ranges: speakingRanges,
        barWidth: getBarWidthFromRanges(speakingBar?.rangeValue, speakingRanges),
        rangeValue: speakingBar?.rangeValue,
      },
      Listening: {
        score: max_point_listening,
        ranges: listeningRanges,
        barWidth: getBarWidthFromRanges(listeningBar?.rangeValue, listeningRanges),
        rangeValue: listeningBar?.rangeValue,
      },
      Reading: {
        score: max_point_reading,
        ranges: readingRanges,
        barWidth: getBarWidthFromRanges(readingBar?.rangeValue, readingRanges),
        rangeValue: readingBar?.rangeValue,
      },
      Writing: {
        score: max_point_writing,
        ranges: writingRanges,
        barWidth: getBarWidthFromRanges(writingBar?.rangeValue, writingRanges),
        rangeValue: writingBar?.rangeValue,
      },
      Comprehension: {
        score: Number(max_point_listening) + Number(max_point_reading),
        ranges: comprehensionRanges,
        barWidth: getBarWidthFromRanges(comprehensionBar?.rangeValue, comprehensionRanges),
        rangeValue: comprehensionBar?.rangeValue,
      },
      Vocal: {
        score: Number(max_point_listening) + Number(max_point_speaking),
        ranges: vocalRanges,
        barWidth: getBarWidthFromRanges(vocalBar?.rangeValue, vocalRanges),
        rangeValue: vocalBar?.rangeValue,
      },
      Literacy: {
        score: Number(max_point_reading) + Number(max_point_writing),
        ranges: literacyRanges,
        barWidth: getBarWidthFromRanges(literacyBar?.rangeValue, literacyRanges),
        rangeValue: literacyBar?.rangeValue,
      },
      Active: {
        score: Number(max_point_speaking) + Number(max_point_writing),
        ranges: activeRanges,
        barWidth: getBarWidthFromRanges(activeBar?.rangeValue, activeRanges),
        rangeValue: activeBar?.rangeValue,
      },
    };

    if (overAllData && overAllData.skills) {
      return overAllData.skills;
    }

    if (submission && submission.skill_scores) {
      return submission.skill_scores;
    }

    return defaultRanges;
  }, [
    submission,
    overAllData,
    domainConfig,
    max_point_speaking,
    max_point_listening,
    max_point_reading,
    max_point_writing,
  ]);

  const getAbsoluteRange = (skillData) => {
    let absoluteMin = Infinity;
    let absoluteMax = -Infinity;

    Object.values(skillData).forEach(({ ranges }) => {
      const min = Math.min(...ranges.map((r) => r[0]));
      const max = Math.max(...ranges.map((r) => r[1]));
      if (min < absoluteMin) absoluteMin = min;
      if (max > absoluteMax) absoluteMax = max;
    });

    return { absoluteMin, absoluteMax };
  };

  const skillAreas = Object.keys(skillData);
  const { absoluteMin, absoluteMax } = getAbsoluteRange(skillData);

  const getSkillAreaTooltip = (area) => {
    const tip = SKILL_AREA_LEGEND.find((s) => s.label === area);
    return tip ? tip.description : area;
  };

  return (
    <>
    <LegendButtonWrap>
      <Tooltip title="Skill area legend">
        <IconButton
          onClick={() => setLegendOpen(true)}
          size="small"
          aria-label="legend"
          sx={{ color: '#1e40af' }}
        >
          <InfoOutlined />
        </IconButton>
      </Tooltip>
    </LegendButtonWrap>
    
    <SkillTableContainer component={Paper}>
      <SkillTable>
        <SkillTableHeader>
          <SkillTableHeaderRow>
            <SkillTableHeaderCell>Skill Area</SkillTableHeaderCell>
            <SkillTableHeaderCell className="scale-score-header">Scale Score</SkillTableHeaderCell>
            {proficiencyLevels.map((level) => (
              <SkillTableHeaderCell key={level.number} className="proficiency-header">
                {level.number}
                <ProficiencyRange>{level.label}</ProficiencyRange>
              </SkillTableHeaderCell>
            ))}
          </SkillTableHeaderRow>
        </SkillTableHeader>
        <TableBody>
          {skillAreas.map((skillArea) => {
            const { score, ranges, barWidth, rangeValue } = skillData[skillArea];

            return (
              <React.Fragment key={skillArea}>
                <SkillTableBodyRow>
                  <SkillTableCell rowSpan={2}>
                    <Tooltip title={getSkillAreaTooltip(skillArea)}>
                      <span>{skillArea}</span>
                    </Tooltip>
                  </SkillTableCell>
                  <SkillTableCell>
                    <ScaleScoreCell>{score}</ScaleScoreCell>
                  </SkillTableCell>
                  {ranges.map((range, index) => (
                    <SkillTableCell key={index}>
                      <ProficiencyLevelCell>
                        <ProficiencyRange>
                          {range[0]}-{range[1]}
                        </ProficiencyRange>
                      </ProficiencyLevelCell>
                    </SkillTableCell>
                  ))}
                </SkillTableBodyRow>
                <SkillTableBodyRow>
                  <SkillTableCell></SkillTableCell>
                  <SkillTableCell colSpan={5} style={{ padding: '8px 16px', borderRight: 'none' }}>
                    <ProficiencyBarWrapper>
                        {score > 0 && rangeValue > 0.0 && (
                            <ProficiencyBarContainer leftPosition={0} width={barWidth}>
                          <ProficiencyBarValue>
                            {Math.round(Number(rangeValue))}
                          </ProficiencyBarValue>
                          </ProficiencyBarContainer>
                        )}
                    </ProficiencyBarWrapper>
                  </SkillTableCell>
                </SkillTableBodyRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </SkillTable>
    </SkillTableContainer>

    <Dialog open={legendOpen} onClose={() => setLegendOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Skill Area Legend
        <IconButton onClick={() => setLegendOpen(false)} aria-label="close" size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {SKILL_AREA_LEGEND.map((item) => (
            <li key={item.label} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <strong>{item.label}</strong>
              {item.label !== item.description && (
                <span style={{ marginLeft: 8 }}>{item.description.replace(item.label, '').trim()}</span>
              )}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
    </>
  );
};

const UserInfoArea = ({ submission, assignment, printLoader, onPrint, proficiencyRows }) => {
  const organization = useCurrentOrg();
  const orgConfig = usePlacementOrgConfig();

  const maxExamPoints = useMemo(() => {
    if (!assignment?.questions?.length) return 0;
    return assignment.questions.reduce((s, q) => s + Number(q.max_point || 0), 0);
  }, [assignment]);

  const overallProficiencyLabel = useMemo(
    () =>
      getOverallProficiencyLabelForSubmission(submission, maxExamPoints, proficiencyRows, {
        orgConfig,
        assignment,
      }),
    [submission, maxExamPoints, proficiencyRows, orgConfig, assignment]
  );

  const showZeroDomainProficiencyNote = useMemo(() => {
    if (!submission?.graded) return false;
    if (!isPlacementZeroDomainCriteriaEnabled(orgConfig)) return false;
    if (!assignment?.questions?.length) return false;
    const speakingMax = sumMaxPointsForCategory(assignment.questions, 'speaking');
    const readingMax = sumMaxPointsForCategory(assignment.questions, 'reading');
    const speakingScore = sumSubmissionCategoryPoints(submission, assignment.questions, 'speaking');
    const readingScore = sumSubmissionCategoryPoints(submission, assignment.questions, 'reading');
    return (
      (speakingMax > 0 && speakingScore === 0) ||
      (readingMax > 0 && readingScore === 0)
    );
  }, [submission, orgConfig, assignment]);

  const zeroScoreLevelNameForNote = useMemo(
    () => getZeroScoreProficiencyLevelName(maxExamPoints, proficiencyRows),
    [maxExamPoints, proficiencyRows],
  );

  const assignmentCodeForNative = useMemo(
    () => assignment?.assigned?.assignment_code || submission?.assignment_code || '',
    [assignment, submission],
  );
  const nativeNonNativeRows = useMemo(
    () => getNativeNonNativeConfigRows(orgConfig, assignmentCodeForNative),
    [orgConfig, assignmentCodeForNative],
  );
  const nativeNonNativeLabel = useMemo(() => {
    if (!submission || (submission.grade !== 0 && !submission.grade)) return '-';
    return getNativeNonNativeLabelFromPoints(submission.grade, maxExamPoints, nativeNonNativeRows);
  }, [submission, maxExamPoints, nativeNonNativeRows]);

  if (!submission || !submission.id || !assignment) return null;
  const fullName = `${submission.firstName} ${submission.lastName}`;
  const { displayName } = assignment;
  const examName = displayName || submission.assignment_code;

  return (
    <UserInfoWrapper>
      <div className="studentInfoUpr">
        <div className="studentInfoLeft">
          <div>
            {orgConfig && orgConfig.logo ? (
              <LogoImg src={orgConfig.logo} alt="Placement District Logo" />
            ) : (
              <DefaultLogoImg src={placement_exam_logo} alt="Placement District Logo" />
            )}
          </div>
          <div className="studentInfoLbl">
            <label>Student Proficiency Report</label>
          </div>
          <div className="studentInfoDate">
            <label>
              <strong>Test Date:</strong>{' '}
              {submission.created_on
                ? moment(submission.created_on).format('MM-DD-YYYY hh:mm A')
                : '-'}
            </label>
          </div>
        </div>
        <div className="studentInfoRght">
          <div>
            <label>
              <strong>Name:</strong> {fullName}
            </label>
          </div>
          <div>
            <label>
              <strong>Student ID:</strong> {submission.student_id}
            </label>
          </div>
          <div>
            <label>
              <strong>School:</strong> {submission.school_name}
            </label>
          </div>
        </div>
        <div className="studentInfoLast">
          <div>
            <label>
              <strong>Grade:</strong>{' '}
              {submission.grade > 0 || submission.grade === 0 ? Math.ceil(submission.grade) : ''}
            </label>
          </div>
          <div>
            <label>
              <strong>Native/NonNative:</strong> {nativeNonNativeLabel}
            </label>
          </div>
          <div>
            <label>
              <strong>Grade Level:</strong> {submission.grade_level || '-'}
            </label>
          </div>
          <div>
            <label>
              <strong>District:</strong> {organization?.displayName || '-'}
            </label>
          </div>
        </div>
       
        <PrintButtonContainer>
          <PrintButton onClick={onPrint}>
            {printLoader ? <Loading size={20} /> : <PrintOutlined style={{ color: '#fff' }} />}
          </PrintButton>
        </PrintButtonContainer>
        <OverallProficiencySection>
              <OverallProficiencyBox>
                <label>
                  <strong>Overall Proficiency:</strong> {overallProficiencyLabel}
                </label>
              </OverallProficiencyBox>
              {showZeroDomainProficiencyNote ? (
                <ZeroDomainProficiencyNote>
                  The student received a score of &quot;0&quot; in one of the domains; therefore, we placed the
                  student at the {zeroScoreLevelNameForNote} proficiency level.
                </ZeroDomainProficiencyNote>
              ) : null}
            </OverallProficiencySection>
      </div>
     
    </UserInfoWrapper>
  );
};

const IndividualStudentProficiencyReport = ({
  submission,
  assignment,
  onOk = () => {},
  overAllData,
  proficiencyRows,
}) => {
  const [printLoader, setPrintloader] = useState(false);
  const imageRef = useRef();

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    disableBodyScroll('IndividualStudentProficiencyReport');

    return () => {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, []);

  const printCall = useReactToPrint({
    contentRef: imageRef,
    onBeforeGetContent: async () => {
      setPrintloader(true);
      if (imageRef.current) {
        imageRef.current.style.height = 'auto';
        imageRef.current.style.overflow = 'visible';
      }
    },
    onBeforePrint: async () => {
      setPrintloader(false);
      if (imageRef.current) {
        imageRef.current.style.height = '';
        imageRef.current.style.overflow = '';
      }
    },
  });

  return (
    <SentryWrap>
      <Wrapper ref={imageRef} className="printable">
        <Section>
          <ProficiencyTitle variant="h4">Individual Student Proficiency Report</ProficiencyTitle>
        </Section>
        <Section>
          <UserInfoArea
            submission={submission}
            assignment={assignment}
            onPrint={printCall}
            printLoader={printLoader}
            proficiencyRows={proficiencyRows}
          />
        </Section>

        <Section>
          <SkillAreaTable
            submission={submission}
            assignment={assignment}
            overAllData={overAllData}
          />
        </Section>
      </Wrapper>
    </SentryWrap>
  );
};

export default IndividualStudentProficiencyReport;
